'use client';

import { useQuery } from '@tanstack/react-query';
import { usePathname } from 'next/navigation';
import apiClient from '@/lib/api';
import { queryKeys } from '@/lib/queryKeys';

const PUBLIC_PATHS = ['/login', '/sign-in'];
const AUTH_DISABLED = process.env.NEXT_PUBLIC_AUTH_DISABLED === 'true';

export interface FeatureFlags {
  modules?: Record<string, boolean>;
  defaults?: Record<string, unknown>;
  thresholds?: Record<string, number>;
}

export interface TenantRole {
  tenant_id: string;
  tenant_slug: string;
  role: string;
  // Effective feature flags for the tenant (defaults <- group <- tenant).
  // Consumed via useFeatures(); absent on older API = all defaults.
  features?: FeatureFlags;
}

export interface MeResponse {
  email: string | null;
  name: string | null;
  is_admin: boolean;
  // Explicit per-user gate for the Alkemy+ aggregated view. Literal value
  // from ``users.group_access`` — admin role does NOT auto-grant. Admins
  // who want the view flip the flag on themselves from the users panel.
  group_access: boolean;
  // Per-user gate for the Budget planner (``users.budget_access``). Same
  // explicit policy: admin role does NOT grant it. The page also needs the
  // tenant's ``modules.budget_plan``. Optional: absent on older API = false.
  budget_access?: boolean;
  roles: TenantRole[];
  // Entities (tenant groups) where the user holds ≥1 tenant membership.
  // Combined with group_access it decides which aggregate views appear.
  // ``features`` = entity-level effective flags, used to gate the group view.
  groups?: { slug: string; name: string; features?: FeatureFlags }[];
}

export function useMe() {
  const pathname = usePathname();
  const isPublic = PUBLIC_PATHS.some((p) => pathname?.startsWith(p));

  return useQuery<MeResponse>({
    queryKey: queryKeys.me(),
    queryFn: () => apiClient.get('/api/v1/me').then((r) => r.data),
    // The user's role doesn't change mid-session in practice; keep it
    // cached aggressively so the FAB and admin gate never wait on a refetch.
    staleTime: Infinity,
    enabled: !isPublic && !AUTH_DISABLED,
  });
}
